import React, { useEffect, useState } from "react";
import "../product-management.scss";
import { SearchBar } from "components/form";
import useDebounce from "common/UseDebounce";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  getMasterBrandData,
  productListApi,
} from "services/product-management";
import Pagination from "react-js-pagination";
import { CommonSelect, Select } from "common/InputForm";
import findSerialNumber from "utils/findSerialNumber";
import NotFound from "common/NotFound";
import useFullPageLoader from "common/UseFullPageLoader";
import { FaEdit, FaTrash, FaRedo } from "react-icons/fa";
import { CustomModal } from "components/modals/ModalBox";
import { userPlaceholder } from "assets/icons";
import { brandListApi } from "services/brand-management";
import { categoryListApi } from "services/category";
import { subCategoryListApi } from "services/subCategory";
import DeleteProduct from "./DeleteProduct";
import AddNewProduct from "./AddProduct";
import { getLimitChar } from "utils/common";
import { userProfilePlaceholder } from "assets/images";

const ProductComponent = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loader, onShow, onHide] = useFullPageLoader();
  const navigate = useNavigate();
  const location = useLocation(); // ⭐ needed to detect "came back from add/edit" signal

  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedProductDetail, setSelectedProductDetail] = useState({});

  const activePage = +searchParams.get("page") || 1;
  const limit = +searchParams.get("limit") || 10;

  const search = searchParams.get("search") || "";
  // Typed text lives in local state so special characters are never altered by
  // the URL round-trip; it is pushed to the URL (and API) after a short debounce.
  const [searchInput, setSearchInput] = useState(search);
  const debouncedInput = useDebounce(searchInput, 300);
  const debouncedValue = useDebounce(search, 300);

  const [fetchedData, setFetchedData] = useState([]);
  const [total, setTotal] = useState(null);

  // ⭐ Filters now read straight from the URL instead of separate local state.
  // This is what makes "whatever I selected" reflected in the URL for free —
  // there's no local copy to fall out of sync with the address bar.
  const filterCategoryState = searchParams.get("category") || "";
  const filterSubCategoryState = searchParams.get("subcategory") || "";
  const filterBrandState = searchParams.get("brand") || "";
  const filterName = searchParams.get("sortByName") || "";

  // ⭐ Single helper to update one or more filter params at once.
  // Passing an empty string removes the param entirely (keeps the URL clean).
  const updateFilterParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    newParams.set("page", 1); // any filter change goes back to page 1
    setSearchParams(newParams);
  };

  // ⭐ Clears every filter (search/category/subcategory/brand/sort) back to defaults.
  const resetFilters = () => {
    setSearchInput("");
    const newParams = new URLSearchParams(searchParams);
    ["search", "category", "subcategory", "brand", "sortByName"].forEach((key) =>
      newParams.delete(key),
    );
    newParams.set("page", 1);
    setSearchParams(newParams);
  };

  // typed text -> URL (debounced)
  useEffect(() => {
    if (debouncedInput !== search) {
      updateFilterParams({ search: debouncedInput });
    }
  }, [debouncedInput]);

  //*********Handel Pagination********* */
  const handlePageChange = (event) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", event);
    setSearchParams(newParams);
  };

  const fetchProductList = async () => {
    const query = {
      page: activePage,
      limit,
      brand: filterBrandState,
      category: filterCategoryState,
      subcategory: filterSubCategoryState,
      sortByName: filterName,
    };

    if (search && debouncedValue) {
      query.search = search;
    }

    try {
      onShow();

      const {
        data: {
          code,
          status,
          data: { total, data },
        },
      } = await productListApi(query);

      if (code === 200 || status) {
        setFetchedData(data);
        setTotal(total);
      }
    } catch (error) {
      console.log(error);
    } finally {
      onHide();
    }
  };

  useEffect(() => {
    fetchProductList();
  }, [
    activePage,
    limit,
    debouncedValue,
    filterBrandState,
    filterCategoryState,
    filterSubCategoryState,
    filterName,
  ]);

  // ⭐ RESET-AFTER-SAVE:
  // When AddNewProduct (used for both add + edit) finishes saving, have it navigate
  // back here like: navigate("/product-management", { state: { resetFilters: true } })
  // We catch that flag, clear every filter, then strip the state so a manual
  // refresh (F5) on this page doesn't keep re-triggering the reset.
  useEffect(() => {
    if (location.state?.resetFilters) {
      resetFilters();
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // -fetch brand, category and subcategory
  const [brandsList, setBrandsList] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  const [subCategoriesList, setSubCategoriesList] = useState([]);

  const fetchBrandLists = async () => {
    try {
      const {
        data: { status, code, data },
      } = await getMasterBrandData();

      if (status || code === 200) {
        setBrandsList(data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchCategoryLists = async () => {
    const query = { status: "ACTIVE", limit: 1000 };
    try {
      const {
        data: {
          status,
          code,
          data: { categories },
        },
      } = await categoryListApi(query);

      if (status || code === 200) {
        setCategoriesList(categories);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchSubCategoryLists = async () => {
    const query = { status: "ACTIVE", limit: 1000 };
    try {
      const {
        data: {
          status,
          code,
          data: { data },
        },
      } = await subCategoryListApi(query);

      if (status || code === 200) {
        setSubCategoriesList(data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchBrandLists();
    fetchCategoryLists();
    fetchSubCategoryLists();
  }, []);

  // -------------show image model
  const [showImagePreviewModel, setShowImagePreviewModel] = useState(false);
  const [imagePreview, setImagePreview] = useState("");

  const showImagePreviewHandler = (profile) => {
    if (profile) {
      setShowImagePreviewModel(true);
      setImagePreview(profile);
    }
  };

  const closeModel = () => {
    setDeleteModal(false);
    setSelectedProductDetail({});
  };

  return (
    <div className="product-container">
      {loader}
      {/* --------add button */}
      <div className="add-button-container">
        <button
          type="button"
          className="button"
          onClick={() => {
            navigate("/product-management/addNewProduct");
          }}
        >
          Add Products
        </button>
      </div>
      {/* --------search box */}
      <div className="search-type-input">
        <div className="search_wrapper">
          <SearchBar
            placeholder={"Search..."}
            onChange={(e) => setSearchInput(e.target.value)}
            value={searchInput}
          />

          {/* ------category filter */}
          <div style={{ minWidth: "220px" }}>
            <CommonSelect
              placeholder="Select Category"
              value={
                categoriesList
                  .map((item) => ({ value: item._id, label: item.name }))
                  .find((item) => item.value === filterCategoryState) || null
              }
              onChange={(selectedOption) => {
                updateFilterParams({ category: selectedOption?.value || "" });
              }}
              options={categoriesList.map((item) => ({
                value: item._id,
                label: item.name,
              }))}
              isSearchable={true}
              isClearable={true}
            />
          </div>

          {/* ------sub category filter */}
          <div style={{ minWidth: "220px" }}>
            <CommonSelect
              placeholder="Select Sub Category"
              value={
                subCategoriesList
                  .map((item) => ({ value: item._id, label: item.name }))
                  .find((item) => item.value === filterSubCategoryState) || null
              }
              onChange={(selectedOption) => {
                updateFilterParams({
                  subcategory: selectedOption?.value || "",
                });
              }}
              options={subCategoriesList.map((item) => ({
                value: item._id,
                label: item.name,
              }))}
              isSearchable={true}
              isClearable={true}
            />
          </div>

          {/* ------brand filter */}
          <div style={{ minWidth: "220px" }}>
            <CommonSelect
              placeholder="Select Brand"
              value={
                brandsList
                  .map((item) => ({ value: item._id, label: item.name }))
                  .find((item) => item.value === filterBrandState) || null
              }
              onChange={(selectedOption) => {
                updateFilterParams({ brand: selectedOption?.value || "" });
              }}
              options={brandsList.map((item) => ({
                value: item._id,
                label: item.name,
              }))}
              isSearchable={true}
              isClearable={true}
            />
          </div>

          <div>
            <Select
              value={filterName}
              onChange={(e) =>
                updateFilterParams({ sortByName: e?.target?.value || "" })
              }
            >
              <option value="">Sort By Name</option>
              <option value="asc">A to Z</option>
              <option value="desc">Z to A</option>
            </Select>
          </div>

          {/* ------reset filters button */}
          <div>
            <button
              type="button"
              className="button reset-filter-button"
              title="Reset filters"
              onClick={resetFilters}
            >
              <FaRedo />
            </button>
          </div>
        </div>
      </div>

      {/* -------table-list -container */}
      <div className="table-container">
        <table className="table custom_table">
          <thead>
            <tr>
              <th className="nowrap text-center">S.No</th>
              <th className="nowrap text-center">Product ID</th>
              <th className="nowrap text-center">Image</th>
              <th className="nowrap text-center">Name</th>
              <th className="nowrap text-center">Brand</th>
              <th className="nowrap text-center">Category</th>
              <th className="nowrap text-center">Sub-Category</th>
              <th className="nowrap text-center">Filter Category</th>
              <th className="nowrap text-center">Action</th>
            </tr>
          </thead>

          <tbody>
            {fetchedData && fetchedData.length > 0 ? (
              fetchedData?.map((mapProp, index) => {
                return (
                  <tr
                    key={index}
                    onClick={() => {
                      navigate(
                        `/product-management/editSelectedProduct/${mapProp._id}`,
                      );
                    }}
                  >
                    <td className="nowrap text-center">
                      {findSerialNumber(index, activePage, limit)}
                    </td>

                    <td
                      className="nowrap text-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {mapProp?.productId || "N/A"}
                    </td>
                    <td className="nowrap text-center">
                      <img
                        src={mapProp?.image[0] || userPlaceholder}
                        alt={`Banner image-${index}`}
                        className="td-img"
                        onClick={(e) => {
                          e.stopPropagation();
                          showImagePreviewHandler(mapProp?.image[0]);
                        }}
                      />
                    </td>

                    <td className="nowrap text-center">
                      {getLimitChar(mapProp?.name, 15)}
                    </td>

                    <td className="nowrap text-center">
                      {(() => {
                        const brandListRes = brandsList.find(
                          (findProp) => findProp._id === mapProp?.brand,
                        );

                        return brandListRes?.name || "---";
                      })()}
                    </td>
                    <td className="nowrap text-center">
                      {mapProp?.category.map((data, ind) => (
                        <div key={ind}>
                          <span>{data.name}</span>
                        </div>
                      )) || "---"}
                    </td>

                    <td className="nowrap text-center">
                      {mapProp?.subcategory.map((subCAtProp, ind) => (
                        <div key={ind}>
                          <span>{subCAtProp.name}</span>
                        </div>
                      )) || "---"}
                    </td>
                    <td className="nowrap text-center">
                      {mapProp?.subsubcategory.map((data, ind) => (
                        <div key={ind}>
                          <span>{data.name}</span>
                        </div>
                      )) || "---"}
                    </td>
                    <td className="nowrap text-center">
                      <div className="button-container">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                              `/product-management/editSelectedProduct/${mapProp._id}`,
                            );
                          }}
                        >
                          <FaEdit />
                        </button>

                        <button
                          type="button"
                          onClick={(e) => {
                            setDeleteModal(true);
                            e.stopPropagation();
                            setSelectedProductDetail(mapProp);
                          }}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <NotFound
                cols={7}
                msg={"Product data not found"}
                onHide={onHide}
              />
            )}
          </tbody>
        </table>
      </div>

      {showImagePreviewModel && (
        <CustomModal
          className={"sm image_preview_modal"}
          show={showImagePreviewModel}
          handleClose={() => {
            setImagePreview("");
            setShowImagePreviewModel(false);
          }}
        >
          <div className="image_preview">
            <img
              src={imagePreview || userPlaceholder}
              alt="user-profile-preview"
            />
          </div>
        </CustomModal>
      )}

      {/* -----delete product   */}
      {deleteModal && (
        <DeleteProduct
          deleteModal={deleteModal}
          selectedProductDetail={selectedProductDetail}
          fetchProductList={fetchProductList}
          closeModel={closeModel}
        />
      )}

      {/* -------------pagination */}
      {total > limit && (
        <div className="pagination-wrapper">
          <div className="Pagination">
            <Pagination
              activePage={activePage}
              previousLabel={"previous"}
              nextLabel={"next"}
              itemsCountPerPage={limit}
              totalItemsCount={total}
              pageRangeDisplayed={5}
              onChange={handlePageChange}
              hideDisabled={true}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductComponent;
